// Trending data for BuddyPass AI.
// Robust multi-source, multi-proxy fetch. Sources: Reddit, Google Trends, HackerNews.

const REDDIT_BASE = "https://www.reddit.com";
// CORS proxies in priority order. r.jina.ai is a generic reader that returns
// the raw page; it's been reliable for Reddit JSON in 2026. The rest are
// classic CORS proxies. We hit them in order and stop on the first 2xx.
const CORS_PROXIES = [
  "https://r.jina.ai/",                          // prefixes the full URL with scheme
  "https://api.codetabs.com/v1/proxy/?quest=",   // querystring URL-encoded
  "https://corsproxy.io/?",                      // querystring URL-encoded
  "https://api.allorigins.win/raw?url=",         // querystring URL-encoded
];

// Wrap a URL with a CORS proxy in the syntax that proxy expects.
function applyProxy(proxy, url) {
  if (proxy === "https://r.jina.ai/") return proxy + url; // raw scheme included
  return proxy + encodeURIComponent(url);
}

const DEFAULT_SUBS = [
  "Entrepreneur",
  "digitalnomad",
  "sidehustle",
  "productivity",
  "DigitalMarketing",
  "personalfinance",
  "povertyfinance",
  "WorkReform",
];

// Pure living-abroad / digital-nomad niche subs for the Partner tab.
// NO finance-dominant subs here — we want relocation, expat life, remote work,
// and travel-logistics conversations.
const NOMAD_SUBS = [
  "digitalnomad",
  "expats",
  "IWantOut",
  "AmericanExpatsUK",
  "expatFIRE",
  "remotework",
  "longtermtravel",
  "solotravel",
  "travel",
  "Portugalexpats",
  "ThailandTourism",
  "Tokyo",
  "mexicoexpats",
];

// Nomad-specific Google / Reddit search queries — real pain points people search.
const NOMAD_SEARCH_QUERIES = [
  "moving abroad mistakes",
  "digital nomad visa",
  "cost of living abroad",
  "remote work abroad",
  "expat culture shock",
  "one way ticket",
  "leaving the US",
  "living in portugal",
  "living in thailand",
  "living in mexico city",
];

const IDEATION_SUBS = [
  "personalfinance",
  "investing",
  "Fitness",
  "AskReddit",
  "relationship_advice",
  "MealPrepSunday",
  "realtors",
  "LifeProTips",
  "getdisciplined",
  "ArtificialIntelligence",
  "chess",
  "languagelearning",
];

async function safeFetch(url, asJson = true, timeoutMs = 4000) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const r = await fetch(url, {
      headers: { Accept: asJson ? "application/json" : "text/plain" },
      cache: "no-store",
      signal: controller.signal,
    });
    if (!r.ok) return null;
    // r.jina.ai returns text; allow asJson:true to still parse if it looks like JSON.
    if (!asJson) return await r.text();
    const text = await r.text();
    try { return JSON.parse(text); } catch { return null; }
  } catch (_) { return null; }
  finally { clearTimeout(timer); }
}

// Fetch a Reddit JSON path with aggressive multi-proxy fallback.
// FAST-FAIL: at most ~12s wall-clock for any one path (4s × up to 3 attempts).
async function redditJson(path) {
  const direct = `${REDDIT_BASE}${path}`;
  // 1) Direct (won't work cross-origin in most browsers but cheap to try once).
  const d0 = await safeFetch(direct, true, 3000);
  if (d0 && (d0?.data?.children || Array.isArray(d0))) return normalizeReddit(d0);
  // 2) Try each proxy ONCE, in priority order — first 2xx wins.
  for (const proxy of CORS_PROXIES) {
    const wrapped = applyProxy(proxy, direct);
    const d = await safeFetch(wrapped, true, 4500);
    if (d && (d?.data?.children || Array.isArray(d))) return normalizeReddit(d);
  }
  return null;
}

function normalizeReddit(d) {
  if (Array.isArray(d)) return { data: { children: [] } }; // Unusable shape
  return d;
}

export async function fetchRedditTop({ subs = DEFAULT_SUBS, timeframe = "week", limit = 6 } = {}) {
  const calls = subs.map(async (sub) => {
    const j = await redditJson(`/r/${sub}/top.json?t=${timeframe}&limit=${limit}`);
    if (!j) return [];
    return (j?.data?.children || []).map((c) => ({
      title: c?.data?.title,
      score: c?.data?.ups || 0,
      subreddit: sub,
      comments: c?.data?.num_comments || 0,
    })).filter((x) => x.title);
  });
  const all = (await Promise.all(calls)).flat();
  return all.sort((a, b) => b.score - a.score).slice(0, 30);
}

export async function fetchRedditSearch({ query, timeframe = "week", limit = 20 }) {
  if (!query) return [];
  const j = await redditJson(`/search.json?q=${encodeURIComponent(query)}&sort=top&t=${timeframe}&limit=${limit}`);
  if (!j) return [];
  return (j?.data?.children || []).map((c) => ({
    title: c?.data?.title,
    score: c?.data?.ups || 0,
    subreddit: c?.data?.subreddit,
    comments: c?.data?.num_comments || 0,
  })).filter((x) => x.title);
}

export async function fetchGoogleTrends({ geo = "US" } = {}) {
  // Google retired /trends/trendingsearches/daily/rss (404). The live
  // equivalent now lives at /trending/rss?geo={X}. We also defensively
  // keep the old URL as a secondary fallback in case Google restores it.
  const urls = [
    `https://trends.google.com/trending/rss?geo=${geo}`,
    `https://trends.google.com/trendingsearches/daily/rss?geo=${geo}`,
    `https://trends.google.com/trends/trendingsearches/daily/rss?geo=${geo}`,
  ];
  const parse = (text) => {
    const itemBlocks = text.match(/<item\b[\s\S]*?<\/item>/g) || [];
    return itemBlocks
      .map((blk) => {
        const tm = blk.match(/<title><!\[CDATA\[([\s\S]*?)\]\]><\/title>/) || blk.match(/<title>([\s\S]*?)<\/title>/);
        const am = blk.match(/<ht:approx_traffic>([\s\S]*?)<\/ht:approx_traffic>/);
        return tm ? { title: tm[1].trim(), traffic: am ? am[1].trim() : "", source: "google_trends" } : null;
      })
      .filter((x) => x && x.title);
  };
  for (const url of urls) {
    for (const proxy of CORS_PROXIES) {
      const text = await safeFetch(proxy + encodeURIComponent(url), false);
      if (!text) continue;
      // Skip proxy landing pages (HTML instead of XML).
      if (!/<rss\b/.test(text) && !/<item\b/.test(text)) continue;
      const items = parse(text);
      if (items.length) return items.slice(0, 25);
    }
  }
  return [];
}

// HackerNews — very CORS-friendly, great for AI/tech/finance signal
export async function fetchHackerNews({ query, hits = 15 } = {}) {
  const url = query
    ? `https://hn.algolia.com/api/v1/search?query=${encodeURIComponent(query)}&tags=story&hitsPerPage=${hits}`
    : `https://hn.algolia.com/api/v1/search?tags=front_page&hitsPerPage=${hits}`;
  const d = await safeFetch(url, true);
  if (!d?.hits) return [];
  return d.hits.map((h) => ({
    title: h.title,
    score: h.points || 0,
    comments: h.num_comments || 0,
    source: "hackernews",
  })).filter((x) => x.title);
}

// YouTube search autocomplete — free, no API key. Surfaces what people are
// actively typing on YouTube (search-intent signal, very close to trending).
// Returns [{ title, source: 'youtube' }] — best-effort; returns [] if blocked.
export async function fetchYouTubeSuggestions({ seeds = [] } = {}) {
  if (!Array.isArray(seeds) || seeds.length === 0) return [];
  const calls = seeds.slice(0, 8).map(async (seed) => {
    const url = `https://suggestqueries.google.com/complete/search?client=youtube&ds=yt&q=${encodeURIComponent(seed)}`;
    // Must go through a CORS proxy that preserves the raw JSONP body.
    for (const proxy of CORS_PROXIES) {
      const text = await safeFetch(proxy + encodeURIComponent(url), false);
      if (!text) continue;
      // Response is JSONP like: window.google.ac.h([seed,[["a",0],["b",0]...],...])
      const m = text.match(/\[(.+)\]/s);
      if (!m) continue;
      try {
        const parsed = JSON.parse("[" + m[1] + "]");
        const suggestions = parsed?.[1] || [];
        return suggestions
          .map((s) => (Array.isArray(s) ? s[0] : s))
          .filter(Boolean)
          .slice(0, 6)
          .map((title) => ({ title, source: "youtube", seed }));
      } catch (_) { /* try next proxy */ }
    }
    return [];
  });
  const all = (await Promise.all(calls)).flat();
  // Deduplicate by title.
  const seen = new Set();
  return all.filter((y) => {
    const key = (y.title || "").toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export function digestTrends({ reddit = [], google = [], hn = [], youtube = [] } = {}) {
  const parts = [];
  if (google.length) {
    parts.push("GOOGLE TRENDS — today's trending searches in the US:\n" + google.slice(0, 15).map((t, i) => `${i + 1}. ${t.title}${t.traffic ? `  (≈${t.traffic} searches)` : ""}`).join("\n"));
  }
  if (youtube.length) {
    parts.push("YOUTUBE SEARCH AUTOCOMPLETE — what people are typing right now:\n" + youtube.slice(0, 20).map((y, i) => `${i + 1}. ${y.title}${y.seed ? `  (seed: ${y.seed})` : ""}`).join("\n"));
  }
  if (reddit.length) {
    parts.push("REDDIT — top posts (real conversations, fresh):\n" + reddit.slice(0, 18).map((r, i) => `${i + 1}. [r/${r.subreddit} · ${r.score}↑ · ${r.comments}💬] ${r.title}`).join("\n"));
  }
  if (hn.length) {
    parts.push("HACKER NEWS — trending (tech/finance/AI pulse):\n" + hn.slice(0, 10).map((h, i) => `${i + 1}. [${h.score}↑ · ${h.comments}💬] ${h.title}`).join("\n"));
  }
  return parts.join("\n\n") || "(no live trending data available this run)";
}

export async function gatherSignalsForNiche({ niche, subs } = {}) {
  // Step 1: ALWAYS try the backend first — same source-of-truth as Partner tab
  // (Reddit via pullpush + Google Trends RSS + YouTube suggest). This was the
  // missing wiring that caused "Grounded in 0 Reddit posts" on Create Your Asset.
  try {
    const fromBackend = await gatherSignalsFromBackend("partner");
    const total = (fromBackend.sources?.reddit_count || 0)
                + (fromBackend.sources?.google_count || 0)
                + (fromBackend.sources?.youtube_count || 0);
    if (total > 0) return fromBackend;
  } catch (e) {
    console.warn("[trends] backend unreachable for niche, falling back to client-side:", e?.message || e);
  }

  // Step 2: legacy client-side fallback (Reddit/Google likely blocked in
  // production, but kept so the app never hard-fails on this step).
  const seeds = niche
    ? [niche, `${niche} problems`, `${niche} mistakes`, `${niche} reality`]
    : [];
  const [searched, topical, google, hn, youtube] = await Promise.all([
    niche ? fetchRedditSearch({ query: niche, timeframe: "week", limit: 20 }) : Promise.resolve([]),
    fetchRedditTop({ subs: subs || DEFAULT_SUBS, timeframe: "week", limit: 6 }),
    fetchGoogleTrends({ geo: "US" }),
    niche ? fetchHackerNews({ query: niche, hits: 10 }) : fetchHackerNews({ hits: 10 }),
    seeds.length ? fetchYouTubeSuggestions({ seeds }) : Promise.resolve([]),
  ]);
  const reddit = [...searched, ...topical].slice(0, 30);
  return {
    reddit, google, hn, youtube,
    digest: digestTrends({ reddit, google, hn, youtube }),
    sources: {
      reddit_count: reddit.length,
      google_count: google.length,
      hn_count: hn.length,
      youtube_count: youtube.length,
    },
  };
}

// Partner / Affiliate tab — LIVING ABROAD + DIGITAL NOMAD niche.
// Uses nomad-specific subs + nomad-specific search queries across Reddit and YouTube.
// Skips HackerNews (tech/finance noise) — those signals don't fit this niche.
// Backend-powered trend gather. Always tries the server-side endpoint first
// because Reddit / Google block direct browser scrapes. If the backend is
// unreachable we fall back to the legacy client-side scrape so the app never
// hard-fails on this step.
async function gatherSignalsFromBackend(slot = "partner") {
  const base = process.env.REACT_APP_BACKEND_URL;
  if (!base) throw new Error("REACT_APP_BACKEND_URL missing");
  const r = await fetch(`${base}/api/trends/${slot}`, {
    method: "GET",
    headers: { Accept: "application/json" },
    cache: "no-store",
  });
  if (!r.ok) throw new Error(`backend trends ${r.status}`);
  const body = await r.json();
  return {
    reddit:  body.raw?.reddit  || [],
    google:  body.raw?.google  || [],
    youtube: body.raw?.youtube || [],
    hn:      [],
    digest:  body.digest || "",
    sources: body.sources || { reddit_count: 0, google_count: 0, youtube_count: 0 },
    cached:  body.cached,
    cache_age_s: body.cache_age_s,
  };
}

export async function gatherSignalsForPartner() {
  // Step 1: try backend (always preferred — bypasses CORS, uses pullpush + RSS).
  try {
    const fromBackend = await gatherSignalsFromBackend("partner");
    const total = (fromBackend.sources?.reddit_count || 0)
                + (fromBackend.sources?.google_count || 0)
                + (fromBackend.sources?.youtube_count || 0);
    if (total > 0) return maybeApplyFallbackCache(fromBackend, "partner");
  } catch (e) {
    console.warn("[trends] backend unreachable, falling back to client-side scrape:", e?.message || e);
  }

  // Step 2: legacy client-side scrape (kept as last-resort fallback).
  const searchCalls = NOMAD_SEARCH_QUERIES.slice(0, 6).map((q) =>
    fetchRedditSearch({ query: q, timeframe: "month", limit: 10 })
  );
  const [searchedNested, topical, google, youtube] = await Promise.all([
    Promise.all(searchCalls),
    fetchRedditTop({ subs: NOMAD_SUBS, timeframe: "week", limit: 5 }),
    fetchGoogleTrends({ geo: "US" }),
    fetchYouTubeSuggestions({ seeds: NOMAD_SEARCH_QUERIES.slice(0, 8) }),
  ]);
  const searched = searchedNested.flat();
  const seen = new Set();
  const reddit = [...searched, ...topical]
    .sort((a, b) => (b.score || 0) - (a.score || 0))
    .filter((r) => {
      const k = (r.title || "").toLowerCase();
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    })
    .slice(0, 30);
  const result = {
    reddit, google, youtube, hn: [],
    digest: digestTrends({ reddit, google, youtube }),
    sources: {
      reddit_count: reddit.length,
      google_count: google.length,
      youtube_count: youtube.length,
      hn_count: 0,
    },
  };
  return maybeApplyFallbackCache(result, "partner");
}

// ─────────────────────────────────────────────────────────────────────
// TREND CACHE — small localStorage ring buffer used as a SAFETY NET.
// Fresh signals are always preferred. Only when the live fetch returns
// zero signals from every source do we splice the most recent cached
// digest into the result so the LLM has *something* trend-grounded to
// work with instead of a blank slate.
// ─────────────────────────────────────────────────────────────────────
const TREND_CACHE_KEY = "bp_trend_cache_v1";
const TREND_CACHE_MAX = 3;

function readTrendCache() {
  try {
    const raw = localStorage.getItem(TREND_CACHE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch { return []; }
}

function writeTrendCache(entry) {
  try {
    const list = readTrendCache();
    list.unshift(entry);
    localStorage.setItem(TREND_CACHE_KEY, JSON.stringify(list.slice(0, TREND_CACHE_MAX)));
  } catch { /* ignore quota errors — caching is best-effort */ }
}

function maybeApplyFallbackCache(result, slot = "partner") {
  const total = (result.sources?.reddit_count || 0)
              + (result.sources?.google_count || 0)
              + (result.sources?.youtube_count || 0);
  if (total > 0) {
    // Live fetch succeeded — persist so we have a fallback next time.
    writeTrendCache({
      slot, ts: Date.now(),
      digest: result.digest, sources: result.sources,
    });
    return { ...result, cache_used: false };
  }
  // Live fetch returned zero — splice in the most recent cached entry.
  const cached = readTrendCache().find((e) => e.slot === slot);
  if (!cached) return { ...result, cache_used: false };
  return {
    ...result,
    digest: `(LIVE TREND FETCH FAILED FOR THIS RUN — using cached signals from ${new Date(cached.ts).toISOString()})\n\n${cached.digest}`,
    sources: { ...result.sources, cache_age_minutes: Math.round((Date.now() - cached.ts) / 60000) },
    cache_used: true,
  };
}

export async function gatherIdeationSignals() {
  // Step 1: backend-first (same fix as gatherSignalsForNiche above).
  try {
    const fromBackend = await gatherSignalsFromBackend("partner");
    const total = (fromBackend.sources?.reddit_count || 0)
                + (fromBackend.sources?.google_count || 0)
                + (fromBackend.sources?.youtube_count || 0);
    if (total > 0) return fromBackend;
  } catch (e) {
    console.warn("[trends] backend unreachable for ideation, falling back to client-side:", e?.message || e);
  }
  // Step 2: legacy client-side fallback.
  const [reddit, google, hn] = await Promise.all([
    fetchRedditTop({ subs: IDEATION_SUBS, timeframe: "week", limit: 5 }),
    fetchGoogleTrends({ geo: "US" }),
    fetchHackerNews({ hits: 15 }),
  ]);
  return {
    reddit, google, hn,
    digest: digestTrends({ reddit, google, hn }),
    sources: {
      reddit_count: reddit.length,
      google_count: google.length,
      hn_count: hn.length,
    },
  };
}
